import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULTS, IYoutubeData, permissionExists, storage, TColorTheme, updateThemeInPage } from '../../init';
import { TNumberWithUndefined, TProcessedVideo, TStringWithUndefined } from '../../interfaces/common';

export interface IAppSlice {
	userPermissions: string[];
	youtubeData: IYoutubeData;
	playedSeconds: number;
	hasAccess: boolean;
	pageIsVisible: boolean;
	accessToken: TStringWithUndefined;
	colorTheme: TColorTheme;
	processedVideo: TProcessedVideo;
	isProcessedMarksEnabled: boolean; // Чи дозволено маркування повідомлень як оброблених
}
const initialState: IAppSlice = {
	userPermissions: [],
	youtubeData: { id: undefined, time: 0 },
	playedSeconds: 0,
	hasAccess: false,
	pageIsVisible: true,
	accessToken: undefined,
	colorTheme: storage.get<TColorTheme>('theme') || 'light',
	processedVideo: storage.get<TProcessedVideo>('processed') || {},
	isProcessedMarksEnabled: storage.get<boolean>('processedMarkEnabled') || false,
};
export const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		changeAccessToken: (state, action: PayloadAction<TStringWithUndefined>) => {
			state.accessToken = action.payload;
		},
		changePermissions: (state, action: PayloadAction<string[] | undefined>) => {
			state.userPermissions = action.payload ? [...action.payload] : [];
			state.hasAccess = permissionExists(DEFAULTS.accessPermissionScope, action.payload);
		},
		changeYoutubeData: (state, action: PayloadAction<IYoutubeData>) => {
			state.youtubeData = action.payload;
			state.playedSeconds = action.payload.time;
		},
		// changeYoutubeId: (state, action: PayloadAction<string | TStringWithUndefined>) => {
		// 	state.youtubeData.id = action.payload;
		// 	state.youtubeData.time = 0;
		// 	state.playedSeconds = 0;
		// },
		// changeYoutubeTime: (state, action: PayloadAction<number>) => {
		// 	state.youtubeData.time = action.payload;
		// 	state.playedSeconds = action.payload;
		// },
		changePlayedSeconds: (state, action: PayloadAction<number>) => {
			state.playedSeconds = action.payload;
		},
		changePageIsVisible: (state, action: PayloadAction<boolean>) => {
			state.pageIsVisible = action.payload;
		},
		changeColorTheme: (state, action: PayloadAction<TColorTheme>) => {
			state.colorTheme = action.payload;
			storage.set({ theme: action.payload });
			updateThemeInPage();
		},
		/**
		 * Додає до storage відмітку про обробку відео.
		 */
		// addProcessedVideo: (state, action: PayloadAction<string>) => {
		// 	const youtubeId = action.payload;
		// 	const newValue = { ...state.processedVideo, [youtubeId]: new Date().getTime() };

		// 	state.processedVideo = newValue;
		// 	storage.set({ processed: newValue });
		// },
		/**
		 * Видаляє зі storage відмітку про обробку відео.
		 */
		// removeProcessedVideo: (state, action: PayloadAction<string>) => {
		// 	const youtubeId = action.payload;
		// 	if (!state.processedVideo[youtubeId]) return;
		// 	const newValue = { ...state.processedVideo };
		// 	delete newValue[youtubeId];

		// 	state.processedVideo = newValue;
		// 	storage.set({ processed: newValue });
		// },
		/**
		 * Змінює в storage відмітку про обробку відео на протилежну.
		 */
		toggleProcessedVideo: (state, action: PayloadAction<string>) => {
			const youtubeId = action.payload;
			const newValue = { ...state.processedVideo };

			if (newValue[youtubeId]) delete newValue[youtubeId];
			else newValue[youtubeId] = new Date().getTime();

			state.processedVideo = newValue;
			storage.setSmart('processed', newValue);
		},
		/**
		 * Видаляє зі storage відмітки про обробку відео, створені понад action.payload секунд
		 * (по замовчанню — 3 доби).
		 */
		sanitizeProcessedVideo: (state, action: PayloadAction<TNumberWithUndefined>) => {
			const MAX_STORAGE_PERIOD = 3; // у днях
			const maxAgeMs = action.payload ?? MAX_STORAGE_PERIOD * 24 * 60 * 60 * 1000;

			const now = Date.now();
			let wasAnyDeleted = false;
			const newValue = { ...state.processedVideo };
			for (const key in newValue) {
				if (now - newValue[key] > maxAgeMs) {
					delete newValue[key];
					wasAnyDeleted = true;
				}
			}

			if (wasAnyDeleted) {
				state.processedVideo = newValue;
				storage.setSmart('processed', newValue);
			}
		},
		toggleProcessingMarkEnabled: (state) => {
			const newEnabled = !state.isProcessedMarksEnabled;
			state.isProcessedMarksEnabled = newEnabled;
			storage.setSmart('processedMarkEnabled', newEnabled);
		},
	},
});

export const {
	changeAccessToken,
	changeYoutubeData,
	changePlayedSeconds,
	changePageIsVisible,
	changePermissions,
	changeColorTheme,
	toggleProcessedVideo,
	sanitizeProcessedVideo,
	toggleProcessingMarkEnabled,
} = appSlice.actions;

export default appSlice.reducer;
